import styles from './search-filters.module.css'

import cuid from 'cuid'
import React, { useLayoutEffect, useRef } from 'react'

import type { FilterDefinition, FilterState } from 'types/filter-page'

import { Button, Text } from 'components/ds'

import AddFilter from './add-filter'
import FilterBadge from './filter-badge'
import {
  buildFixedFilterStates,
  computeFilterChange,
  computeFilterDelete,
  getFilterBadgeKey,
} from './utils'

type ClearAllFilterProps = {
  definitions: FilterDefinition[]
  onChange: (_value: FilterState[]) => void
  disabled?: boolean
  seedDefaultState?: boolean
}

type SearchFilterProps = {
  definitions: FilterDefinition[]
  value: FilterState[]
  onChange: (_value: FilterState[]) => void
}

type SelectedFilterBadgesProps = SearchFilterProps & {
  seedDefaultState?: boolean
}

export const FilterActionAddButton = ({
  definitions,
  value,
  onChange,
}: SearchFilterProps): React.ReactNode => {
  const handleAddFilter = (definition: FilterDefinition) =>
    onChange([
      ...value,
      {
        id: cuid(),
        definition,
        operator: undefined,
        value: undefined,
      },
    ])

  return (
    <AddFilter
      definitions={definitions}
      onAddFilter={handleAddFilter}
      currentFilterStates={value}
    />
  )
}

export const FilterActionClearAllButton = ({
  onChange,
  definitions,
  disabled,
  seedDefaultState = false,
}: ClearAllFilterProps): React.ReactNode => {
  // On clear all, remove all non-fixed filters and clear values of fixed filters
  // Applies to all filters values, including those that are hidden
  const handleClearAll = () => {
    if (!seedDefaultState) {
      onChange([])
      return
    }

    const fixedFilters = definitions.filter(filter => filter.fixed)
    const clearedFilters = fixedFilters.map(filter => ({
      id: cuid(),
      definition: filter,
      operator: undefined,
      value: undefined,
    }))
    onChange(clearedFilters)
  }

  return (
    <Button
      variant="ghost"
      className={styles.clearAllButton}
      onClick={handleClearAll}
      disabled={disabled}
      size="2"
    >
      <Text as="span" size={{ initial: '1', sm: '2' }}>
        Reset
      </Text>
    </Button>
  )
}
export const SelectedFilterBadges = ({
  value,
  onChange,
  definitions,
  seedDefaultState = false,
}: SelectedFilterBadgesProps) => {
  const hasSeeded = useRef(false)

  useLayoutEffect(() => {
    if (!seedDefaultState) {
      return
    }
    if (hasSeeded.current || value.length) return
    hasSeeded.current = true

    onChange(buildFixedFilterStates(definitions))
  }, [])

  const handleFilterChange = (index: number, newValue: FilterState): void =>
    onChange(computeFilterChange(value, index, newValue))

  const handleFilterDelete = (index: number): void =>
    onChange(computeFilterDelete(value, index))

  // flatMap keeps original indexes so edits and deletes hit the right filter
  const filterBadges = value.flatMap((filterState, i) =>
    filterState.hidden
      ? []
      : [
          <FilterBadge
            key={getFilterBadgeKey(filterState, i)}
            value={filterState}
            onChange={value => handleFilterChange(i, value)}
            onDelete={() => handleFilterDelete(i)}
          />,
        ],
  )

  return filterBadges
}
